import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import type { ReactNode } from 'react';
import { cn } from '../../../../lib/utils.js';

export type DropdownMenuItem = {
  key: string;
  label: string;
  onSelect?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  icon?: ReactNode;
};

export type DropdownMenuProps = {
  trigger: ReactNode;
  items: DropdownMenuItem[];
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  mobileSheet?: boolean;
  className?: string;
};

export function DropdownMenu({
  trigger,
  items,
  align = 'end',
  side = 'bottom',
  mobileSheet = false,
  className,
}: DropdownMenuProps) {
  return (
    <RadixDropdownMenu.Root>
      <RadixDropdownMenu.Trigger asChild>{trigger}</RadixDropdownMenu.Trigger>
      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content
          align={align}
          side={side}
          sideOffset={8}
          collisionPadding={8}
          className={cn(
            'z-50 min-w-[220px] overflow-hidden rounded-lg border border-[var(--border-default)] bg-[var(--bg-elevated)] p-1 shadow-[0_16px_48px_rgba(0,0,0,0.45)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            mobileSheet ? 'fixed inset-x-0 bottom-0 rounded-t-sheet md:relative md:inset-auto md:rounded-lg' : '',
            className
          )}
        >
          {items.map((item) => (
            <RadixDropdownMenu.Item
              key={item.key}
              disabled={item.disabled}
              onSelect={() => item.onSelect?.()}
              className={cn(
                'relative flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-body-14 outline-none',
                item.destructive ? 'text-[var(--danger)]' : 'text-[var(--text-primary)]',
                'data-[highlighted]:bg-[var(--bg-secondary)] data-[highlighted]:text-[var(--text-primary)]',
                'data-[disabled]:pointer-events-none data-[disabled]:opacity-45'
              )}
            >
              {item.icon ? <span className="text-[var(--text-secondary)]">{item.icon}</span> : null}
              {item.label}
            </RadixDropdownMenu.Item>
          ))}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
}

